import { useCallback, useContext, useState, useEffect, useRef, RefObject } from "react"
import {
  ReactFlow,
  addEdge,
  Background,
  Controls,
  type Edge,
  type OnConnect,
  type OnNodeDrag,
  type NodeChange,
  Panel,
  useReactFlow,
  ConnectionMode,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import { HStack, Icon, IconButton, Text, VStack, Spinner, Box } from "@chakra-ui/react"
import { Alert } from "../../../shared/react/src/chakra/Alert"
import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa"
import { FaSquare, FaLock, FaLockOpen, FaPlus } from "react-icons/fa6"
import AssetNode from "./nodes/Asset"
import ApiAssetNode from "./nodes/ApiAsset"
import InstallationGroup from "./nodes/InstallationGroup"
import { SystemViewData, SystemNode as SystemNodeData } from "../types/SystemView"
import { transformSystemViewToReactFlow, withoutLoadPrecedence } from "../utils/systemTransform"
import AssignAssetsBlankSlate from "./AssignAssetsBlankSlate"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { ToggleSystemStatusDocument } from "@hydrologiq/gql/graphql"
import { GetSystemViewDocument, CreateAssetAssignmentDocument, DeleteSystemNodeDocument } from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useIsSystemInlineMonitoring, useEditMode } from "../providers/helpers/useSystem"
import { SupabaseContext, RealtimeContext, NodesEdgesContext } from "../providers/SystemProvider"
import StatusToggleWorkflow, {
  WorkflowState,
  WorkflowMessages,
} from "../../../shared/react/src/chakra/StatusToggleWorkflow"
import { SystemNodeVersion } from "../types/SystemNode"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useSectionColorMode } from "@hydrologiq/shared/chakra/ColorMode"
import RealtimeStatusIndicator from "./RealtimeStatusIndicator"
import AddAssetDialog from "./AddAssetDialog"
import Dialog from "../../../shared/react/src/chakra/Dialog"
import { ActionButton, CancelButton } from "../../../shared/react/src/content/components/Button"

const isUUID = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)

/**
 * Determines if a system node has never been manually placed.
 * The backend sets both created_at and updated_at to now() on INSERT.
 * The update_system_node_positions RPC is the only thing that updates updated_at.
 * If they're within 2 seconds, the node was never dragged by a user.
 */
const isNodeUnplaced = (node: SystemNodeData): boolean => {
  if (!node.createdAt || !node.updatedAt) return false
  const created = new Date(node.createdAt)
  const updated = new Date(node.updatedAt)
  return Math.abs(updated.getTime() - created.getTime()) < 2000
}

/**
 * Given a delta (dx, dy) from one node to another, determine which handle ID
 * (top/right/bottom/left) best represents that direction.
 */
const getHandleIdFromDirection = (dx: number, dy: number): string => {
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 ? "right" : "left"
  }
  return dy > 0 ? "bottom" : "top"
}

const nodeTypes = {
  asset: AssetNode, // Keep existing node type for hardcoded systems
  apiAsset: ApiAssetNode, // New node type for API systems
  group: InstallationGroup, // Group node type for installations
}

interface ApiFlowProps {
  systemId: string
  systemData?: SystemViewData
  loading?: boolean
  error?: string
  refetch: () => void
  container?: RefObject<HTMLElement | null>
}

const ApiFlow = ({ systemId, systemData, loading, error, refetch, container }: ApiFlowProps) => {
  const { nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange, appliedSnapshotRef } =
    useContext(NodesEdgesContext)
  const { isEditMode, setEditMode } = useEditMode()
  const { fitView } = useReactFlow()
  const isGrouped = useIsSystemInlineMonitoring()
  const systemStatus = systemData?.system.status
  const [toggleSystemStatus, { loading: toggleSystemStatusLoading }] = useMutationWithEndpoint(
    ToggleSystemStatusDocument,
    { refetchQueries: [GetSystemViewDocument] },
    "primary"
  )

  const [createAssetAssignment] = useMutationWithEndpoint(
    CreateAssetAssignmentDocument,
    {},
    "primary"
  )

  const [deleteSystemNode, { loading: deleteNodeLoading }] = useMutationWithEndpoint(
    DeleteSystemNodeDocument,
    {},
    "primary"
  )

  // System status workflow state
  const [workflowState, setWorkflowState] = useState<WorkflowState>("idle")
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null)

  // Asset picker state
  const [assetPickerOpen, setAssetPickerOpen] = useState(false)

  // Removal confirmation dialog state
  const [removalDialog, setRemovalDialog] = useState<{
    open: boolean
    nodeId: string
    assignmentId: string
    assetName: string
    isMonitoringActive: boolean
  } | null>(null)
  const [removalError, setRemovalError] = useState<string | null>(null)

  // Node IDs that are "unplaced" (never manually dragged) — highlighted with a
  // pulsing border until the user moves them. The backend sets initial positions
  // via calculate_position at creation time, so no frontend auto-placement needed.
  const highlightedNodeIdsRef = useRef<Set<string>>(new Set())

  // Generate workflow messages based on action
  const getWorkflowMessages = (action: "activate" | "deactivate"): WorkflowMessages => ({
    processing: `Please wait while the system is being ${action}d...`,
    cancelling: `Cancelling the ${action} request...`,
    success: `System has been successfully ${action}d.`,
    error: `Failed to ${action} system. Please try again or contact support.`,
  })

  // Handle system status toggle
  const handleSystemStatusToggle = () => {
    const action = systemStatus === "active" ? "deactivate" : "activate"
    setPendingAction(action)
    setWorkflowState("confirming")
  }

  // Handle workflow confirmation
  const handleConfirmToggle = () => {
    if (!pendingAction) return

    setWorkflowState("processing")

    toggleSystemStatus({ variables: { systemId } })
      .then(() => {
        setWorkflowState("success")
        // Note: refetch and dialog close will be handled when user manually closes success dialog
      })
      .catch((error) => {
        console.error("Failed to toggle system status:", error)
        setWorkflowState("error")
      })
  }

  // Handle workflow cancellation
  const handleCancelToggle = () => {
    setWorkflowState("idle")
    setPendingAction(null)
  }

  // Handle dialog close
  const handleCloseDialog = () => {
    // Don't allow close during processing or cancelling
    if (workflowState === "processing" || workflowState === "cancelling") {
      return
    }

    // If closing from success state, transition to confirmed state for cleanup
    if (workflowState === "success") {
      setWorkflowState("confirmed")
      return
    }

    // For all other states, just close normally
    setWorkflowState("idle")
    setPendingAction(null)
  }

  // Handle confirmed state - perform cleanup and refresh
  useEffect(() => {
    if (workflowState === "confirmed") {
      const performCleanup = async () => {
        // Refresh system data
        await refetch()

        // Reset workflow state
        setWorkflowState("idle")
        setPendingAction(null)
      }

      performCleanup()
    }
  }, [workflowState, refetch])

  const graphsEnabled = useGetFeature("system_node_graphs")
  const acLoadEnabled = useGetFeature("ac_load")
  const colorMode = useSectionColorMode()
  const source = systemData?.system?.source ?? "timescale"
  const nodeVersion = useGetFeature("system_node_v2") ? SystemNodeVersion.V2 : SystemNodeVersion.V1

  // Transform API data to React Flow format when systemData changes.
  // Each snapshot is applied exactly once: nodes/edges outlive this component, so
  // re-applying a snapshot on remount would discard any arrangement made since it
  // was fetched. A genuine refetch produces a new object reference and re-applies.
  useEffect(() => {
    if (systemData && appliedSnapshotRef.current !== systemData) {
      appliedSnapshotRef.current = systemData
      const allSystemNodes = [
        ...systemData.installations.flatMap((inst) => inst.nodes),
        ...systemData.isolatedNodes,
      ]

      // Identify nodes that have never been manually positioned (created_at ≈ updated_at).
      // The backend assigns initial positions at creation time, so these don't need
      // repositioning — we just highlight them so the user knows they're new.
      highlightedNodeIdsRef.current = new Set(
        allSystemNodes.filter((n) => isNodeUnplaced(n)).map((n) => n.id)
      )

      const { nodes: transformedNodes, edges: transformedEdges } = transformSystemViewToReactFlow(
        acLoadEnabled ? systemData : withoutLoadPrecedence(systemData),
        container,
        isGrouped,
        graphsEnabled,
        nodeVersion,
        true
      )

      // Convert nodes to use the appropriate node type
      const apiNodes = transformedNodes.map((node) => {
        // Keep group nodes as 'group' type, convert others to 'apiAsset'
        if (node.type === "group") {
          return node
        }

        // Determine which specific handles on this node are used by connections.
        // For each edge, compute the direction from this node to the other node
        // to identify which handle (top/right/bottom/left) the edge uses.
        const connectedHandleIds = new Set<string>()
        for (const edge of transformedEdges) {
          if (edge.source === node.id) {
            // If edge has an explicit sourceHandle, use it
            if (edge.sourceHandle) {
              connectedHandleIds.add(edge.sourceHandle)
            } else {
              // Determine direction from this node to the target
              const targetNode = transformedNodes.find((n) => n.id === edge.target)
              if (targetNode) {
                const dx = targetNode.position.x - node.position.x
                const dy = targetNode.position.y - node.position.y
                connectedHandleIds.add(getHandleIdFromDirection(dx, dy))
              }
            }
          }
          if (edge.target === node.id) {
            // If edge has an explicit targetHandle, use it
            if (edge.targetHandle) {
              connectedHandleIds.add(edge.targetHandle)
            } else {
              // Determine direction from this node to the source
              const sourceNode = transformedNodes.find((n) => n.id === edge.source)
              if (sourceNode) {
                const dx = sourceNode.position.x - node.position.x
                const dy = sourceNode.position.y - node.position.y
                connectedHandleIds.add(getHandleIdFromDirection(dx, dy))
              }
            }
          }
        }

        return {
          ...node,
          type: "apiAsset",
          // Preserve all critical properties like parentId, extent, and handle positions
          parentId: node.parentId,
          extent: node.extent,
          sourcePosition: node.sourcePosition,
          targetPosition: node.targetPosition,
          data: {
            ...node.data,
            systemId,
            refetch,
            nodeType: systemData.installations
              .flatMap((inst) => inst.nodes)
              .concat(systemData.isolatedNodes)
              .find((n) => n.id === node.id)?.type,
            container,
            disabled: systemStatus === "inactive",
            version: nodeVersion,
            connectedHandleIds: connectedHandleIds.size > 0 ? connectedHandleIds : undefined,
          },
          className: highlightedNodeIdsRef.current.has(node.id) ? "react-flow__node--new" : undefined,
        }
      })

      setNodes(apiNodes)

      // Patch edges that don't have explicit sourceHandle/targetHandle so the
      // connection line routes to the correct handle (not the node default).
      const nodePositionMap = new Map(transformedNodes.map((n) => [n.id, n.position]))
      const patchedEdges = transformedEdges.map((edge) => {
        let { sourceHandle, targetHandle } = edge
        if (!sourceHandle) {
          const sourcePos = nodePositionMap.get(edge.source)
          const targetPos = nodePositionMap.get(edge.target)
          if (sourcePos && targetPos) {
            const dx = targetPos.x - sourcePos.x
            const dy = targetPos.y - sourcePos.y
            sourceHandle = getHandleIdFromDirection(dx, dy)
          }
        }
        if (!targetHandle) {
          const sourcePos = nodePositionMap.get(edge.source)
          const targetPos = nodePositionMap.get(edge.target)
          if (sourcePos && targetPos) {
            const dx = sourcePos.x - targetPos.x
            const dy = sourcePos.y - targetPos.y
            targetHandle = getHandleIdFromDirection(dx, dy)
          }
        }
        if (sourceHandle !== edge.sourceHandle || targetHandle !== edge.targetHandle) {
          return { ...edge, sourceHandle, targetHandle }
        }
        return edge
      })

      setEdges(patchedEdges)

      // If there are new (unplaced) nodes, center the viewport on them
      // and default to unlocked so the user can reposition them.
      if (highlightedNodeIdsRef.current.size > 0) {
        setEditMode(true)
        const newNodeIds = highlightedNodeIdsRef.current
        requestAnimationFrame(() => {
          fitView({
            nodes: apiNodes.filter((n) => newNodeIds.has(n.id)),
            padding: 0.3,
            duration: 300,
          })
        })
      }
    }
  }, [systemData])

  // Node data captures the canvas container ref, which is recreated when the System
  // view remounts. Refresh it without touching positions or structure.
  useEffect(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, container } })))
  }, [container, setNodes])

  // ─── Debounced position persistence ────────────────────────────────────────
  const supabase = useContext(SupabaseContext)
  const { addRecentWrite, markDragging, clearDragging } = useContext(RealtimeContext)

  const onConnect: OnConnect = useCallback(
    async (connection) => {
      // Determine if both connected nodes have active monitoring for styling
      const sourceNode = nodes.find((n) => n.id === connection.source)
      const targetNode = nodes.find((n) => n.id === connection.target)
      const sourceActive = (sourceNode?.data as Record<string, unknown>)?.assignmentStatus === "active"
      const targetActive = (targetNode?.data as Record<string, unknown>)?.assignmentStatus === "active"
      const bothActive = sourceActive && targetActive

      const styledConnection = {
        ...connection,
        animated: bothActive,
        style: bothActive
          ? { stroke: "#39aed2", strokeWidth: 2 }
          : { stroke: "#a9a9a9", strokeWidth: 2 },
      }

      // Optimistic: add edge to local state immediately with correct styling
      setEdges((eds) => addEdge(styledConnection, eds))

      // If Supabase is not configured, just keep the local edge without persisting
      if (!supabase) return

      const tenantId = systemData?.system?.tenant
      if (!tenantId) return

      // Persist via Supabase INSERT — get the created row's ID for deduplication
      const { data, error } = await supabase
        .from("system_connection")
        .insert({
          source_node: connection.source,
          target_node: connection.target,
          source_handle: connection.sourceHandle ?? null,
          target_handle: connection.targetHandle ?? null,
          tenant: tenantId,
        })
        .select("id")
        .single()

      if (error) {
        // Rollback the optimistic edge
        setEdges((eds) =>
          eds.filter((e) => !(e.source === connection.source && e.target === connection.target))
        )
      } else if (data?.id) {
        // Replace the synthetic ReactFlow edge ID with the real database UUID
        // so that future deletion operations use the correct ID
        setEdges((eds) =>
          eds.map((e) =>
            e.source === connection.source && e.target === connection.target && !isUUID(e.id)
              ? { ...e, id: data.id }
              : e
          )
        )
        // Add connection ID to deduplication set so realtime subscription skips the echo
        addRecentWrite(data.id)
      }
    },
    [setEdges, supabase, systemData, addRecentWrite, nodes]
  )

  // Handle edge deletion — optimistic removal + Supabase DELETE
  const onEdgesDelete = useCallback(
    async (edgesToDelete: Edge[]) => {
      if (!supabase) return

      for (const edge of edgesToDelete) {
        // Add to deduplication set so realtime subscription skips the echo
        addRecentWrite(edge.id)

        // Persist deletion to Supabase
        const { error } = await supabase
          .from("system_connection")
          .delete()
          .eq("id", edge.id)

        if (error) {
          console.error("Failed to delete connection:", error)
          // Rollback: re-add the edge
          setEdges((eds) => [...eds, edge])
        }
      }
    },
    [supabase, addRecentWrite]
  )

  // Custom onNodesChange that intercepts node removal to show confirmation dialog
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Separate remove changes from everything else
      const removeChanges = changes.filter((c) => c.type === "remove")
      const otherChanges = changes.filter((c) => c.type !== "remove")

      // Apply non-remove changes normally (position, selection, dimensions, etc.)
      if (otherChanges.length > 0) {
        onNodesChange(otherChanges)
      }

      // For remove changes, open the confirmation dialog instead of removing directly.
      // Unassignment (DEL key) is independent of the layout lock (isEditMode) — it's
      // always available so users can unassign assets regardless of edit mode.
      if (removeChanges.length > 0) {
        const nodeId = (removeChanges[0] as { id: string }).id
        const node = nodes.find((n) => n.id === nodeId)
        if (!node || node.type === "group") return

        const nodeData = node.data as Record<string, unknown>
        const assignment = nodeData?.assignment as { id?: string; status?: string } | undefined
        const isMonitoringActive = assignment?.status === "active"
        const assetName = nodeData?.asset
          ? (nodeData.asset as { name?: string })?.name
          : (nodeData?.label as string) ?? "this asset"

        setRemovalDialog({
          open: true,
          nodeId: node.id,
          assignmentId: assignment?.id ?? "",
          assetName: assetName ?? "this asset",
          isMonitoringActive: isMonitoringActive ?? false,
        })
      }
    },
    [onNodesChange, nodes]
  )

  // Accumulates position updates — overwrites same-node updates within the debounce window
  const pendingPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map())
  // Timer ID for the 300ms debounce
  const positionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Flush all accumulated positions to the database
  const flushPositionUpdates = useCallback(() => {
    const pending = pendingPositionsRef.current
    if (pending.size === 0 || !supabase) return

    const updates = Array.from(pending.entries()).map(([id, pos]) => ({
      id,
      x: pos.x,
      y: pos.y,
    }))

    // Mark all IDs as recent writes for deduplication before sending
    for (const { id } of updates) {
      addRecentWrite(id)
    }

    // Clear the pending map
    pending.clear()

    // Fire the RPC — errors are logged but not surfaced (position corrects on next poll)
    supabase
      .rpc("update_system_node_positions", { p_updates: updates })
      .then(({ error }) => {
        if (error) {
          console.error("Failed to persist node positions:", error)
        }
      })
  }, [supabase, addRecentWrite])

  // Drag start handler — marks the node so realtime updates don't snap it away
  const onNodeDragStart: OnNodeDrag = useCallback(
    (_event, node) => {
      markDragging(node.id)
    },
    [markDragging]
  )

  // Drag stop handler — accumulates and debounces
  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, node) => {
      // Clear the dragging guard — the deduplication set handles echo suppression from here
      clearDragging(node.id)

      // Once the user manually moves a node, it's no longer "new" — clear its highlight
      if (highlightedNodeIdsRef.current.has(node.id)) {
        highlightedNodeIdsRef.current.delete(node.id)
        setNodes((nds) =>
          nds.map((n) => (n.id === node.id ? { ...n, className: undefined } : n))
        )
      }

      // Group nodes don't have their own system_node row — skip them
      if (node.type === "group") return

      // Calculate the absolute position to persist
      let absoluteX = node.position.x
      let absoluteY = node.position.y

      // For child nodes (inside an installation group), ReactFlow reports position
      // relative to the parent. Convert to absolute by adding the parent's position.
      if (node.parentId) {
        const parentNode = nodes.find((n) => n.id === node.parentId)
        if (parentNode) {
          absoluteX += parentNode.position.x
          absoluteY += parentNode.position.y
        }
      }

      // Add/overwrite this node's position in the pending map
      pendingPositionsRef.current.set(node.id, {
        x: absoluteX,
        y: absoluteY,
      })

      // Reset the debounce timer
      if (positionTimerRef.current !== null) {
        clearTimeout(positionTimerRef.current)
      }
      positionTimerRef.current = setTimeout(() => {
        positionTimerRef.current = null
        flushPositionUpdates()
      }, 300)
    },
    [flushPositionUpdates, nodes, clearDragging, setNodes]
  )

  // Flush pending position updates whenever layout is locked (isEditMode transitions to false)
  useEffect(() => {
    if (!isEditMode) {
      if (positionTimerRef.current !== null) {
        clearTimeout(positionTimerRef.current)
        positionTimerRef.current = null
      }
      flushPositionUpdates()
    }
  }, [isEditMode, flushPositionUpdates])

  // Clean up timers and flush remaining position updates on unmount
  useEffect(() => {
    return () => {
      if (positionTimerRef.current !== null) {
        clearTimeout(positionTimerRef.current)
        positionTimerRef.current = null
      }
      flushPositionUpdates()
    }
  }, [flushPositionUpdates])

  // Assign a single asset to this system — called by the dialog per asset.
  // Use a ref-based callback to avoid recreating when systemData/createAssetAssignment change.
  const assignAssetRef = useRef<(assetId: string) => Promise<void>>(async () => {})
  assignAssetRef.current = async (assetId: string) => {
    const customerId = systemData?.system?.customerId
    const siteId = systemData?.system?.siteId

    if (!customerId || !siteId) {
      throw new Error("Missing customerId or siteId for asset assignment")
    }

    await createAssetAssignment({
      variables: {
        input: {
          assetId,
          systemId,
          customerId,
          siteId,
        },
      },
    })
  }

  const handleAssignAsset = useCallback(
    async (assetId: string) => {
      await assignAssetRef.current(assetId)
    },
    []
  )

  // Called by the dialog after all assets are assigned and provisioning wait is done.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  const handleAssignComplete = useCallback(async () => {
    await refetchRef.current()
    setAssetPickerOpen(false)
  }, [])

  // Stable close handler for the asset picker dialog
  const handleAssetPickerClose = useCallback(() => {
    setAssetPickerOpen(false)
  }, [])

  // Loading state
  if (loading) {
    return (
      <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading system data...</Text>
        </VStack>
      </Box>
    )
  }

  // Error state
  if (error) {
    return (
      <Box width="100%" height="100%" padding={4}>
        <Alert status="error">
          <Icon as={FaExclamationTriangle} color="red.500" mr={2} />
          Failed to load system data: {error}
        </Alert>
      </Box>
    )
  }

  // No data state
  if (!systemData) {
    return (
      <Box width="100%" height="100%" padding={4}>
        <Alert status="info">
          <Icon as={FaInfoCircle} color="blue.500" mr={2} />
          No system data available for ID: {systemId}
        </Alert>
      </Box>
    )
  }

  // Blank slate — no assets have been assigned to this system yet.
  // Check both systemData (initial load) and local ReactFlow nodes state (after removals)
  // so the blank slate appears immediately when the last node is removed without waiting for a refetch.
  const hasAssetNodesFromData =
    systemData.installations.some((group) => group.nodes.length > 0) || systemData.isolatedNodes.length > 0
  const hasAssetNodesLocal = nodes.some((n) => n.type !== "group")
  if (!hasAssetNodesFromData || !hasAssetNodesLocal) {
    return (
      <>
        <AssignAssetsBlankSlate onAssignAssets={() => setAssetPickerOpen(true)} />
        <AddAssetDialog
          open={assetPickerOpen}
          onClose={handleAssetPickerClose}
          assignAsset={handleAssignAsset}
          onComplete={handleAssignComplete}
          container={container}
        />
      </>
    )
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={handleNodesChange}
      onEdgesChange={isEditMode ? onEdgesChange : undefined}
      onConnect={isEditMode ? onConnect : undefined}
      onEdgesDelete={isEditMode ? onEdgesDelete : undefined}
      onNodeDragStart={isEditMode ? onNodeDragStart : undefined}
      onNodeDragStop={isEditMode ? onNodeDragStop : undefined}
      nodeTypes={nodeTypes}
      connectionMode={ConnectionMode.Loose}
      connectionLineStyle={{ stroke: "#39aed2", strokeWidth: 2 }}
      // Delete is only available in edit mode
      deleteKeyCode={isEditMode ? ["Delete", "Backspace"] : null}
      defaultEdgeOptions={{ interactionWidth: 20 }}
      edgesFocusable={isEditMode}
      fitView
      /* React Flow's default minZoom of 0.5 is too tight for real systems: Burnaston's 21 nodes
         span ~1348x862px, which needs zoom ~0.39 to fit the 400px-min canvas. Clamped at 0.5 the
         content renders taller than the viewport and fitView centres the overflow, clipping top
         and bottom. Padding additionally covers AlertBadge, which sits at top:-14px and so falls
         outside the measured node bounds fitView works from. */
      minZoom={0.2}
      fitViewOptions={{ padding: 0.15 }}
      style={{ backgroundColor: "transparent" }}
      colorMode={colorMode}
      nodesDraggable={isEditMode}
      nodesConnectable={isEditMode}
      elementsSelectable={isEditMode}
      className={!isEditMode ? "react-flow--locked" : "react-flow--editing"}
    >
      <Controls showInteractive={false} position="top-right" />

      {/* Realtime connection status indicator (hidden when connected) */}
      <RealtimeStatusIndicator />



      {/* Status Legend Panel */}
      {nodeVersion === SystemNodeVersion.V1 && <Panel position="bottom-left">
        <HStack data-testid="legend" ml={"50px"} gap={"8px"}>
          <HStack gap={"3px"}>
            <Icon color={"#39aed2"} as={FaSquare} />
            <Text fontSize={"sm"}>Active</Text>
          </HStack>
          <HStack gap={"3px"}>
            <Icon color={"#a9a9a9"} as={FaSquare} />
            <Text fontSize={"sm"}>Inactive</Text>
          </HStack>
        </HStack>
      </Panel>}


      <Panel position="top-left">
        <VStack
          gap={0}
          bg="card"
          borderRadius="md"
          boxShadow="sm"
          border="1px solid"
          borderColor="border"
          p={1.5}
          align="center"
          minWidth="60px"
        >
          <VStack
            gap={0}
            align="center"
            cursor="pointer"
            color="primary"
            onClick={() => setAssetPickerOpen(true)}
            _hover={{ color: "blue" }}
            role="button"
            aria-label="Add assets"
          >
            <IconButton
              aria-hidden="true"
              size="xs"
              variant="ghost"
              pointerEvents="none"
              tabIndex={-1}
              color="inherit"
            >
              <Icon as={FaPlus} />
            </IconButton>
            <Text fontSize="2xs" textAlign="center" lineHeight="1.2" mb={1} color="inherit">
              Add<br />assets
            </Text>
          </VStack>
          <Box width="100%" height="1px" bg="gray.200" />
            <VStack
              gap={0}
              align="center"
              cursor="pointer"
              color="primary"
              onClick={() => setEditMode(!isEditMode)}
              _hover={{ color: "blue" }}
              role="button"
              aria-label={isEditMode ? "Lock layout" : "Unlock to edit"}
              mt={1}
            >
              <IconButton
                aria-hidden="true"
                size="xs"
                variant="ghost"
                color="inherit"
                pointerEvents="none"
                tabIndex={-1}
              >
                {isEditMode ? <FaLockOpen /> : <FaLock />}
              </IconButton>
              <Text fontSize="2xs" textAlign="center" lineHeight="1.2" color="inherit">
                {isEditMode ? <>Lock<br />layout</> : <>Unlock<br />to edit</>}
              </Text>
            </VStack>
        </VStack>
      </Panel>

      <Background />

      {/* Add Asset Dialog */}
      <AddAssetDialog
        open={assetPickerOpen}
        onClose={handleAssetPickerClose}
        assignAsset={handleAssignAsset}
        onComplete={handleAssignComplete}
        container={container}
      />

      {/* Removal Confirmation Dialog */}
      <Dialog
        title="Remove Asset"
        open={removalDialog?.open ?? false}
        setOpen={(open) => {
          if (!open) {
            setRemovalDialog(null)
            setRemovalError(null)
          }
        }}
        container={container}
        showCloseButton={false}
        footer={
          <>
            <CancelButton onClick={() => { setRemovalDialog(null); setRemovalError(null) }} disabled={deleteNodeLoading} />
            <ActionButton
              context={{
                label: "Remove",
                description: "Confirm removal from system",
                variant: "primary",
              }}
              colorPalette="red"
              disabled={deleteNodeLoading}
              loading={deleteNodeLoading}
              onClick={async () => {
                if (!removalDialog) return
                setRemovalError(null)

                const { nodeId } = removalDialog

                try {
                  // deleteSystemNode handles both cases:
                  // - If the node has an active assignment, runs full unassign lifecycle
                  // - If orphaned (no assignment), deletes the node directly
                  const { errors } = await deleteSystemNode({ variables: { nodeId } })
                  if (errors?.length) {
                    throw new Error(errors[0].message)
                  }

                  // Remove the node from local state immediately (don't wait for realtime)
                  setNodes((nds) => nds.filter((n) => n.id !== nodeId))
                  // Also remove any edges connected to this node
                  setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
                  // Add to dedup set so if a realtime DELETE arrives later it's a no-op
                  addRecentWrite(nodeId)

                  setRemovalDialog(null)
                  setRemovalError(null)
                } catch (err: any) {
                  console.error("Failed to remove asset:", err)
                  setRemovalError(err?.message ?? "Failed to remove asset. Please try again.")
                }
              }}
            />
          </>
        }
      >
        <Text>
          {removalDialog?.isMonitoringActive
            ? `This will deactivate monitoring and remove ${removalDialog.assetName} from the system.`
            : `This will remove ${removalDialog?.assetName ?? "this asset"} from the system.`}
        </Text>
        {removalError && (
          <Text color="red.500" fontSize="sm" mt={2}>
            {removalError}
          </Text>
        )}
      </Dialog>
    </ReactFlow>
  )
}

export default ApiFlow
